package ru.chm.gradereport.data

import io.ktor.client.HttpClient
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.coroutines.CancellationException
import ru.chm.gradereport.model.AssignCoursesRequest
import ru.chm.gradereport.model.AttendanceDto
import ru.chm.gradereport.model.CheckInSessionDto
import ru.chm.gradereport.model.CourseAnalyticsDto
import ru.chm.gradereport.model.CourseDto
import ru.chm.gradereport.model.CourseFormsSettingsDto
import ru.chm.gradereport.model.CreateCheckInRequest
import ru.chm.gradereport.model.CreateCourseRequest
import ru.chm.gradereport.model.CreateHomeworkRequest
import ru.chm.gradereport.model.UpdateHomeworkRequest
import ru.chm.gradereport.model.UpdateCourseRequest
import ru.chm.gradereport.model.CreateSessionRequest
import ru.chm.gradereport.model.UpdateSessionRequest
import ru.chm.gradereport.model.CreateStudentRequest
import ru.chm.gradereport.model.CreateReviewerRequest
import ru.chm.gradereport.model.ErrorResponse
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.HomeworkDto
import ru.chm.gradereport.model.HomeworkGradeDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.model.LoginRequest
import ru.chm.gradereport.model.LoginResponse
import ru.chm.gradereport.model.MessageResponse
import ru.chm.gradereport.model.MarkSubmissionsSeenRequest
import ru.chm.gradereport.model.PublicCheckInInfoDto
import ru.chm.gradereport.model.ResetPasswordRequest
import ru.chm.gradereport.model.SetAttendanceRequest
import ru.chm.gradereport.model.SetGradeRequest
import ru.chm.gradereport.model.StudentDto
import ru.chm.gradereport.model.StudentStatsDto
import ru.chm.gradereport.model.SubmitCheckInRequest
import ru.chm.gradereport.model.ReviewerDto
import ru.chm.gradereport.model.UpdateCourseFormsSettingsRequest
import ru.chm.gradereport.model.UpdateHomeworkSubmissionRequest
import ru.chm.gradereport.model.UpdateStudentRequest
import ru.chm.gradereport.model.UserDto
import ru.chm.gradereport.platform.getApiBaseUrl
import ru.chm.gradereport.platform.loadAuthToken
import ru.chm.gradereport.platform.saveAuthToken

class ApiException(val status: HttpStatusCode, message: String) : Exception(message)

class GradeApi(
    baseUrl: String = getApiBaseUrl(),
) {
    private val root = baseUrl.trimEnd('/')

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
        explicitNulls = true
    }

    private val client = HttpClient {
        expectSuccess = false
        install(ContentNegotiation) {
            json(json)
        }
        defaultRequest {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Accept, ContentType.Application.Json.toString())
        }
    }

    var token: String? = loadAuthToken()
        private set

    fun setToken(value: String?) {
        token = value
        saveAuthToken(value)
    }

    private fun url(path: String): String {
        val normalized = if (path.startsWith("/")) path else "/$path"
        return root + normalized
    }

    suspend fun login(login: String, password: String): LoginResponse = network {
        val payload = json.encodeToString(LoginRequest.serializer(), LoginRequest(login, password))
        val response = client.post(url("/api/auth/login")) {
            setBody(payload)
            contentType(ContentType.Application.Json)
        }
        response.decode()
    }

    suspend fun me(): UserDto = network { authedGet("/api/auth/me") }

    suspend fun logout() {
        runCatching { network { authedPostEmpty("/api/auth/logout") } }
        setToken(null)
    }

    suspend fun listCourses(): List<CourseDto> = network { authedGet("/api/courses") }

    suspend fun getCourse(courseId: String): CourseDto =
        network { authedGet("/api/courses/$courseId") }

    suspend fun createCourse(name: String, description: String): CourseDto =
        network { authedPost("/api/courses", CreateCourseRequest(name, description)) }

    suspend fun deleteCourse(courseId: String) {
        network { authedDelete("/api/courses/$courseId") }
    }

    suspend fun updateCourse(courseId: String, name: String, description: String): CourseDto =
        network { authedPut("/api/courses/$courseId", UpdateCourseRequest(name, description)) }

    suspend fun getSheet(courseId: String): GradeSheetDto =
        network { authedGet("/api/courses/$courseId/sheet") }

    suspend fun getAnalytics(courseId: String): CourseAnalyticsDto =
        network { authedGet("/api/courses/$courseId/analytics") }

    suspend fun getStudentStats(courseId: String, studentId: String): StudentStatsDto =
        network { authedGet("/api/courses/$courseId/students/$studentId/stats") }

    suspend fun addStudent(courseId: String, fullName: String, studentNumber: String): StudentDto =
        network {
            authedPost("/api/courses/$courseId/students", CreateStudentRequest(fullName, studentNumber))
        }

    suspend fun updateStudent(
        courseId: String,
        studentId: String,
        fullName: String,
        studentNumber: String,
    ): StudentDto = network {
        authedPut(
            "/api/courses/$courseId/students/$studentId",
            UpdateStudentRequest(fullName, studentNumber),
        )
    }

    suspend fun deleteStudent(courseId: String, studentId: String) {
        network { authedDelete("/api/courses/$courseId/students/$studentId") }
    }

    suspend fun addSession(
        courseId: String,
        date: String,
        title: String,
        description: String = "",
    ): LessonSessionDto =
        network {
            authedPost(
                "/api/courses/$courseId/sessions",
                CreateSessionRequest(date, title, description),
            )
        }

    suspend fun updateSession(
        courseId: String,
        sessionId: String,
        date: String,
        title: String,
        description: String = "",
    ): LessonSessionDto =
        network {
            authedPut(
                "/api/courses/$courseId/sessions/$sessionId",
                UpdateSessionRequest(date, title, description),
            )
        }

    suspend fun deleteSession(courseId: String, sessionId: String) {
        network { authedDelete("/api/courses/$courseId/sessions/$sessionId") }
    }

    suspend fun setAttendance(courseId: String, request: SetAttendanceRequest): AttendanceDto =
        network { authedPut("/api/courses/$courseId/attendance", request) }

    suspend fun createCheckIn(
        courseId: String,
        sessionId: String,
        ttlMinutes: Int,
        pageBaseUrl: String,
    ): CheckInSessionDto =
        network {
            authedPost(
                "/api/courses/$courseId/checkin",
                CreateCheckInRequest(
                    sessionId = sessionId,
                    ttlMinutes = ttlMinutes,
                    pageBaseUrl = pageBaseUrl,
                ),
            )
        }

    suspend fun getActiveCheckIn(courseId: String, pageBaseUrl: String): CheckInSessionDto? = network {
        val encoded = pageBaseUrl.encodeURLParameter()
        val response = client.get(url("/api/courses/$courseId/checkin/active?pageBaseUrl=$encoded")) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
        }
        if (response.status == HttpStatusCode.NoContent) return@network null
        response.decode()
    }

    suspend fun clearCheckIn(courseId: String) {
        network { authedDelete("/api/courses/$courseId/checkin") }
    }

    suspend fun publicCheckInInfo(token: String): PublicCheckInInfoDto =
        network { publicGet("/api/public/checkin/$token") }

    suspend fun submitPublicCheckIn(token: String, studentId: String): AttendanceDto =
        network {
            publicPost(
                "/api/public/checkin/$token",
                SubmitCheckInRequest(studentId),
            )
        }

    suspend fun addHomework(
        courseId: String,
        title: String,
        description: String,
        maxScore: Double,
        sessionId: String,
    ): HomeworkDto =
        network {
            authedPost(
                "/api/courses/$courseId/homeworks",
                CreateHomeworkRequest(
                    title = title,
                    description = description,
                    maxScore = maxScore,
                    sessionId = sessionId,
                ),
            )
        }

    suspend fun updateHomework(
        courseId: String,
        homeworkId: String,
        title: String,
        description: String,
        maxScore: Double,
        sessionId: String,
    ): HomeworkDto =
        network {
            authedPut(
                "/api/courses/$courseId/homeworks/$homeworkId",
                UpdateHomeworkRequest(
                    title = title,
                    description = description,
                    maxScore = maxScore,
                    sessionId = sessionId,
                ),
            )
        }

    suspend fun deleteHomework(courseId: String, homeworkId: String) {
        network { authedDelete("/api/courses/$courseId/homeworks/$homeworkId") }
    }

    suspend fun setGrade(courseId: String, request: SetGradeRequest): HomeworkGradeDto =
        network { authedPut("/api/courses/$courseId/grades", request) }

    /** Edit the answer link of an existing homework submission. */
    suspend fun updateSubmission(courseId: String, submissionId: String, url: String) {
        network<Unit> {
            authedPut(
                "/api/courses/$courseId/submissions/$submissionId",
                UpdateHomeworkSubmissionRequest(url),
            )
        }
    }

    /** Delete a homework submission link. */
    suspend fun deleteSubmission(courseId: String, submissionId: String) {
        network<Unit> { authedDelete("/api/courses/$courseId/submissions/$submissionId") }
    }

    /** Acknowledge pending answers of a cell (clears the purple highlight). */
    suspend fun markSubmissionsSeen(courseId: String, studentId: String, homeworkId: String) {
        network<Unit> {
            authedPost(
                "/api/courses/$courseId/submissions/seen",
                MarkSubmissionsSeenRequest(studentId, homeworkId),
            )
        }
    }

    suspend fun listReviewers(): List<ReviewerDto> = network { authedGet("/api/reviewers") }

    suspend fun createReviewer(login: String, password: String, displayName: String): ReviewerDto =
        network {
            authedPost("/api/reviewers", CreateReviewerRequest(login, password, displayName))
        }

    suspend fun deleteReviewer(reviewerId: String) {
        network { authedDelete("/api/reviewers/$reviewerId") }
    }

    suspend fun resetReviewerPassword(reviewerId: String, password: String): MessageResponse =
        network {
            authedPut("/api/reviewers/$reviewerId/password", ResetPasswordRequest(password))
        }

    suspend fun assignCourses(reviewerId: String, courseIds: List<String>): ReviewerDto =
        network { authedPut("/api/reviewers/$reviewerId/courses", AssignCoursesRequest(courseIds)) }

    suspend fun getCourseFormsSettings(courseId: String): CourseFormsSettingsDto =
        network { authedGet("/api/courses/$courseId/forms-settings") }

    suspend fun updateCourseFormsSettings(
        courseId: String,
        formsBaseUrl: String,
        formsBearerToken: String? = null,
    ): CourseFormsSettingsDto =
        network {
            authedPut(
                "/api/courses/$courseId/forms-settings",
                UpdateCourseFormsSettingsRequest(
                    formsBaseUrl = formsBaseUrl,
                    formsBearerToken = formsBearerToken,
                ),
            )
        }

    suspend fun publicSheet(token: String): GradeSheetDto =
        network { publicGet("/api/public/$token/sheet") }

    /** Homework-only page via the separate homework token (no grades/attendance inside). */
    suspend fun publicHomeworkSheet(token: String): GradeSheetDto =
        network { publicGet("/api/homework/$token") }

    suspend fun publicStudentStats(token: String, studentId: String): StudentStatsDto =
        network { publicGet("/api/public/$token/students/$studentId/stats") }

    suspend fun publicStudentStatsByUid(token: String, studentUid: String): StudentStatsDto =
        network { publicGet("/api/public/$token/students/uid/$studentUid/stats") }

    private suspend inline fun <T> network(block: suspend () -> T): T =
        try {
            block()
        } catch (e: CancellationException) {
            throw e
        } catch (e: ApiException) {
            throw e
        } catch (e: Throwable) {
            val details = e.message?.takeIf { it.isNotBlank() } ?: e::class.simpleName ?: "unknown"
            throw ApiException(
                HttpStatusCode.ServiceUnavailable,
                "Нет связи с API ($details). Запустите сервер: ./gradlew :server:run — $root",
            )
        }

    private suspend inline fun <reified T> authedGet(path: String): T {
        val response = client.get(url(path)) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
        }
        return response.decode()
    }

    private suspend inline fun <reified T> publicGet(path: String): T {
        val response = client.get(url(path))
        return response.decode()
    }

    private suspend inline fun <reified T> publicPost(path: String, body: Any): T {
        val response = client.post(url(path)) {
            setBody(body)
        }
        return response.decode()
    }

    private suspend inline fun <reified T> authedPost(path: String, body: Any): T {
        val response = client.post(url(path)) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
            setBody(body)
        }
        return response.decode()
    }

    private suspend fun authedPostEmpty(path: String) {
        val response = client.post(url(path)) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
        }
        if (!response.status.isSuccess()) {
            throw ApiException(response.status, parseError(response.status, response.bodyAsText()))
        }
    }

    private suspend inline fun <reified T> authedPut(path: String, body: Any): T {
        val response = client.put(url(path)) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
            setBody(body)
        }
        return response.decode()
    }

    private suspend fun authedDelete(path: String) {
        val response = client.delete(url(path)) {
            token?.let { header(HttpHeaders.Authorization, "Bearer $it") }
        }
        if (!response.status.isSuccess()) {
            throw ApiException(response.status, parseError(response.status, response.bodyAsText()))
        }
    }

    private suspend inline fun <reified T> HttpResponse.decode(): T {
        val raw = bodyAsText()
        if (!status.isSuccess()) {
            throw ApiException(status, parseError(status, raw))
        }
        return try {
            json.decodeFromString(raw)
        } catch (e: Exception) {
            throw ApiException(
                status,
                "Не удалось разобрать ответ сервера: ${e.message ?: "unknown"}",
            )
        }
    }

    private fun parseError(status: HttpStatusCode, raw: String): String {
        val fromBody = extractErrorMessage(raw)
        if (fromBody != null) return fromBody
        return defaultMessageForStatus(status)
    }

    private fun extractErrorMessage(raw: String): String? {
        val trimmed = raw.trim()
        if (trimmed.isEmpty()) return null

        runCatching {
            json.decodeFromString<ErrorResponse>(trimmed).message
        }.getOrNull()?.let { message ->
            if (isHumanReadableError(message)) return message.trim()
        }

        runCatching {
            json.parseToJsonElement(trimmed).jsonObject["message"]?.jsonPrimitive?.contentOrNull
        }.getOrNull()?.let { message ->
            if (isHumanReadableError(message)) return message.trim()
        }

        // Plain text body from older servers / proxies.
        if (isHumanReadableError(trimmed) && !trimmed.startsWith('{') && !trimmed.startsWith('<')) {
            return trimmed.take(300)
        }
        return null
    }

    private fun isHumanReadableError(message: String): Boolean {
        val value = message.trim()
        if (value.isEmpty()) return false
        if (value.contains("ErrorResponse", ignoreCase = true)) return false
        if (value.startsWith("ru.") || value.startsWith("ru/")) return false
        if (value.contains('/') && value.contains('.') && !value.contains(' ')) return false
        return true
    }

    private fun defaultMessageForStatus(status: HttpStatusCode): String = when (status.value) {
        400 -> "Некорректный запрос. Проверьте введённые данные."
        401 -> "Сессия истекла или вы не авторизованы. Войдите снова."
        403 -> "Недостаточно прав для этого действия."
        404 -> "Данные не найдены. Проверьте ссылку, курс или идентификатор студента."
        409 -> "Конфликт данных. Возможно, такая запись уже есть."
        500, 502, 503 -> "Сервер временно недоступен. Попробуйте позже."
        else -> "Ошибка запроса (${status.value}). Попробуйте ещё раз."
    }
}
